/* ==========================================================================
   Maštarije Nova — mst-product.js
   PDP runtime. Custom elements (editor-safe: they re-initialize whenever the
   section is re-rendered because the elements are re-created):

     <mst-variant-picker data-section data-url>
       Pill radios + embedded variants JSON. On change: updates the hidden
       id input, price (MST.formatMoney), availability/submit label, URL
       (replaceState), gallery (variant image), quantity rules; recomputes
       crossed-out pills; dispatches 'mst:variant:changed' {variant, sectionId}.

     <mst-product-form data-section>
       AJAX add-to-cart: POST /cart/add.js with
       sections=cart-drawer,cart-icon-bubble → patches
       [data-cart-drawer-inner] + #cart-icon-bubble-wrap (contract §6),
       dispatches 'mst:cart:updated' {cart} and opens the drawer.
       Listens on the WRAPPER (bubble phase) so the personalizer's
       capture-phase submit validation on the form always runs first and
       can cancel it (preventDefault + stopPropagation) — plus an explicit
       defaultPrevented guard. Without a cart drawer on the page it lets
       the native POST happen (no-JS-equivalent path).

     <mst-media-gallery data-section>
       Mobile (<990px): scroll-snap carousel + arrows + "X/Y" counter.
       Desktop (≥990px): 2-column tile grid — goTo() scrolls the tile into
       view (page scroll) and highlights it (.is-active). Exposes
       setActiveMedia(mediaId); announces "slika X od Y" via aria-live.

     <mst-sticky-atc data-section>
       Ported from mastarije-theme/snippets/mst-sticky-atc.liquid: mirrors
       the price wrapper (#price-<section>, MutationObserver), shows when
       the REAL submit button scrolls out of view (IntersectionObserver)
       and triggers that real button so personalizer validation runs.

     <mst-recommendations data-url>
       Lazily fetches the related-products section (Section Rendering API)
       when it approaches the viewport.
   ========================================================================== */

(function () {
  'use strict';

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function formatMoney(cents) {
    if (window.MST && typeof window.MST.formatMoney === 'function') {
      return window.MST.formatMoney(cents);
    }
    return (cents / 100).toFixed(2);
  }

  function patchSectionHTML(sectionHTML, sourceSelector, target) {
    if (!sectionHTML || !target) return;
    var doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    var source = sourceSelector ? doc.querySelector(sourceSelector) : doc.body;
    if (source) target.innerHTML = source.innerHTML;
  }

  /* ------------------------------------------------------------------------
     <mst-variant-picker>
     ------------------------------------------------------------------------ */
  if (!customElements.get('mst-variant-picker')) {
    customElements.define(
      'mst-variant-picker',
      class MstVariantPicker extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.section || '';
          this.url = this.dataset.url || '';
          this.variants = [];

          var json = this.querySelector('script[data-variants]');
          if (json) {
            try {
              this.variants = JSON.parse(json.textContent);
            } catch (error) {
              this.variants = [];
            }
          }

          this.onChangeBound = this.onChange.bind(this);
          this.addEventListener('change', this.onChangeBound);

          // Liquid computes the initial crossed-out states against
          // selected_or_first_available_variant — when a different default
          // is preselected (wooden-frame default) they can be stale.
          this.updateAvailability(this.getSelectedOptions());
        }

        disconnectedCallback() {
          this.removeEventListener('change', this.onChangeBound);
        }

        fieldsets() {
          return Array.prototype.slice.call(
            this.querySelectorAll('fieldset[data-option-position]')
          );
        }

        getSelectedOptions() {
          var selected = [];
          this.fieldsets().forEach(function (fieldset) {
            var position = parseInt(fieldset.dataset.optionPosition, 10);
            var checked = fieldset.querySelector('input[type="radio"]:checked');
            selected[position - 1] = checked ? checked.value : null;
          });
          return selected;
        }

        findVariant(selected) {
          return (
            this.variants.find(function (variant) {
              return selected.every(function (value, index) {
                return variant.options[index] === value;
              });
            }) || null
          );
        }

        onChange(event) {
          if (!event.target.matches('input[type="radio"]')) return;

          var selected = this.getSelectedOptions();
          var variant = this.findVariant(selected);

          this.updateLegends();
          this.updateAvailability(selected);
          this.updateForm(variant);
          this.announcePrice(variant);
          this.updateGallery(variant);
          this.updateUrl(variant);

          var detail = { variant: variant, sectionId: this.sectionId };
          document.dispatchEvent(
            new CustomEvent('mst:variant:changed', { detail: detail })
          );
          if (window.MST && window.MST.pubsub) {
            window.MST.pubsub.publish('mst:variant:changed', detail);
          }
        }

        updateLegends() {
          this.fieldsets().forEach(function (fieldset) {
            var checked = fieldset.querySelector('input[type="radio"]:checked');
            var out = fieldset.querySelector('[data-selected-value]');
            if (checked && out) out.textContent = checked.value;
          });
        }

        updateAvailability(selected) {
          var variants = this.variants;
          this.fieldsets().forEach(function (fieldset) {
            var position = parseInt(fieldset.dataset.optionPosition, 10);
            fieldset
              .querySelectorAll('input[type="radio"]')
              .forEach(function (input) {
                var available = variants.some(function (variant) {
                  if (!variant.available) return false;
                  if (variant.options[position - 1] !== input.value) return false;
                  return selected.every(function (value, index) {
                    if (index === position - 1) return true;
                    if (!value) return true;
                    return variant.options[index] === value;
                  });
                });
                var pill = input.closest('.mst-variant-pill');
                if (pill) pill.classList.toggle('is-unavailable', !available);
                var sr = pill && pill.querySelector('[data-unavailable-sr]');
                if (sr) sr.hidden = available;
              });
          });
        }

        updateForm(variant) {
          var form = document.getElementById('product-form-' + this.sectionId);
          var submit = document.getElementById(
            'ProductSubmitButton-' + this.sectionId
          );
          var label = submit && submit.querySelector('[data-submit-label]');

          if (form && variant) {
            var idInput = form.querySelector('input[type="hidden"][name="id"]');
            if (idInput) idInput.value = variant.id;
          }

          if (submit && label) {
            if (!variant) {
              submit.disabled = true;
              label.textContent = submit.dataset.unavailableText || '';
            } else if (!variant.available) {
              submit.disabled = true;
              label.textContent = submit.dataset.soldoutText || '';
            } else {
              submit.disabled = false;
              label.textContent = submit.dataset.addText || '';
            }
          }

          if (variant) {
            this.renderPrice(variant);
            this.updateQuantityRules(variant);
          }
        }

        renderPrice(variant) {
          var wrap = document.getElementById('price-' + this.sectionId);
          if (!wrap) return;
          var container = wrap.querySelector('[data-price-container]') || wrap;

          var onSale =
            variant.compare_at_price != null &&
            variant.compare_at_price > variant.price;
          var regularText = wrap.dataset.regularText || '';
          var saleText = wrap.dataset.saleText || '';

          var html = '';
          if (onSale) {
            html +=
              '<span class="visually-hidden">' + saleText + '</span>' +
              '<span class="mst-price__current">' + formatMoney(variant.price) + '</span>' +
              '<span class="visually-hidden">' + regularText + '</span>' +
              '<s class="mst-price__compare">' + formatMoney(variant.compare_at_price) + '</s>';
          } else {
            html +=
              '<span class="visually-hidden">' + regularText + '</span>' +
              '<span class="mst-price__current">' + formatMoney(variant.price) + '</span>';
          }

          if (variant.unit_price != null && variant.unit_price_measurement) {
            var reference = variant.unit_price_measurement.reference_value;
            var unit = variant.unit_price_measurement.reference_unit || '';
            var unitLabel = wrap.dataset.unitLabel || '';
            var unitSeparator = wrap.dataset.unitSeparator || '';
            // Mirror snippets/price.liquid: visually hidden "Unit price"
            // label + SR-readable separator so the per-unit price keeps its
            // meaning after a variant change.
            html +=
              '<small class="mst-price__unit">' +
              '<span class="visually-hidden">' + unitLabel + '</span>' +
              formatMoney(variant.unit_price) +
              '<span aria-hidden="true">/</span>' +
              '<span class="visually-hidden">' + unitSeparator + '</span>' +
              (reference && reference !== 1 ? reference : '') +
              unit +
              '</small>';
          }

          container.innerHTML =
            '<div class="mst-price mst-product__price-value' +
            (onSale ? ' mst-price--sale' : '') +
            '">' +
            html +
            '</div>';
        }

        /* accessibility-09: price, sold-out and unavailable states change
           without a sound otherwise (price block's live region). */
        announcePrice(variant) {
          var wrap = document.getElementById('price-' + this.sectionId);
          var live = wrap && wrap.querySelector('[data-price-live]');
          if (!live) return;
          var submit = document.getElementById('ProductSubmitButton-' + this.sectionId);
          var text = '';
          if (!variant) {
            text = (submit && submit.dataset.unavailableText) || '';
          } else {
            text = (live.dataset.template || '[price]').replace('[price]', formatMoney(variant.price));
            if (!variant.available && submit && submit.dataset.soldoutText) {
              text += '. ' + submit.dataset.soldoutText;
            }
          }
          live.textContent = text;
        }

        updateQuantityRules(variant) {
          var input = document.getElementById('Quantity-' + this.sectionId);
          if (!input || !variant || !variant.quantity_rule) return;

          var rule = variant.quantity_rule;
          input.min = rule.min || 1;
          if (rule.max) {
            input.max = rule.max;
          } else {
            input.removeAttribute('max');
          }
          input.step = rule.increment || 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        updateGallery(variant) {
          // Gallery tiles are keyed by data-media-id="{{ media.id }}" —
          // a MEDIA id. variant.featured_media.id lives in that namespace;
          // variant.featured_image.id is a ProductImage id and never matches.
          if (!variant || !variant.featured_media) return;
          var gallery = document.getElementById('MediaGallery-' + this.sectionId);
          if (gallery && typeof gallery.setActiveMedia === 'function') {
            gallery.setActiveMedia(variant.featured_media.id);
          }
        }

        updateUrl(variant) {
          if (!variant || !this.url || !window.history.replaceState) return;
          window.history.replaceState(
            {},
            '',
            this.url + '?variant=' + variant.id
          );
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     <mst-product-form>
     ------------------------------------------------------------------------ */
  if (!customElements.get('mst-product-form')) {
    customElements.define(
      'mst-product-form',
      class MstProductForm extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form');
          this.submitBtn = this.form
            ? this.form.querySelector('button[type="submit"]')
            : null;
          this.errorEl = this.querySelector('[data-form-error]');

          // The form ships WITHOUT novalidate so native constraint validation
          // guards the no-JS / pre-hydration path (required personalizer
          // fields). With JS live, the personalizer's styled capture-phase
          // validation takes over.
          if (this.form) this.form.noValidate = true;

          this.onSubmitBound = this.onSubmit.bind(this);
          // Bubble phase on the wrapper: the personalizer's capture-phase
          // listener on the form itself ALWAYS runs first and can cancel.
          this.addEventListener('submit', this.onSubmitBound);
        }

        disconnectedCallback() {
          this.removeEventListener('submit', this.onSubmitBound);
        }

        onSubmit(event) {
          if (event.defaultPrevented) return;
          if (!this.form) return;

          var drawer = document.getElementById('MstCartDrawer');
          // No drawer (cart type = page): let the native POST handle it.
          if (!drawer) return;

          event.preventDefault();

          if (
            this.submitBtn &&
            (this.submitBtn.disabled ||
              this.submitBtn.getAttribute('aria-busy') === 'true')
          ) {
            return;
          }

          var self = this;
          if (this.errorEl) {
            this.errorEl.hidden = true;
            this.errorEl.textContent = '';
          }
          if (this.submitBtn) this.submitBtn.setAttribute('aria-busy', 'true');

          var addUrl = this.form.getAttribute('action') || '/cart/add';
          if (addUrl.indexOf('.js') === -1) addUrl += '.js';
          var cartUrl = addUrl.replace(/\/add\.js$/, '.js');

          var body = new FormData(this.form);
          body.append('sections', 'cart-drawer,cart-icon-bubble');
          body.append('sections_url', window.location.pathname);

          this.duplicateCheck(body, cartUrl)
            .then(function () {
              return fetch(addUrl, {
                method: 'POST',
                headers: {
                  Accept: 'application/javascript',
                  'X-Requested-With': 'XMLHttpRequest'
                },
                body: body
              });
            })
            .then(function (response) {
              return response.json();
            })
            .then(function (data) {
              if (data.status && data.status !== 200) {
                throw new Error(data.description || data.message || '');
              }

              if (data.sections) {
                patchSectionHTML(
                  data.sections['cart-drawer'],
                  '[data-cart-drawer-inner]',
                  document.querySelector('[data-cart-drawer-inner]')
                );
                patchSectionHTML(
                  data.sections['cart-icon-bubble'],
                  '.shopify-section',
                  document.getElementById('cart-icon-bubble-wrap')
                );
              }

              return fetch(cartUrl, { headers: { Accept: 'application/json' } })
                .then(function (response) {
                  return response.json();
                })
                .then(function (cart) {
                  var detail = { cart: cart };
                  document.dispatchEvent(
                    new CustomEvent('mst:cart:updated', { detail: detail })
                  );
                  if (window.MST && window.MST.pubsub) {
                    window.MST.pubsub.publish('mst:cart:updated', detail);
                  }
                })
                .catch(function () {
                  document.dispatchEvent(
                    new CustomEvent('mst:cart:updated', {
                      detail: { cart: null }
                    })
                  );
                })
                .then(function () {
                  if (typeof drawer.open === 'function') {
                    drawer.open(self.submitBtn);
                  } else {
                    drawer.setAttribute('open', '');
                  }
                });
            })
            .catch(function (error) {
              if (self.errorEl) {
                self.errorEl.textContent =
                  (error && error.message) ||
                  self.errorEl.dataset.genericError ||
                  '';
                self.errorEl.hidden = false;
                // Sent from the sticky bar, the message would sit off-screen.
                var box = self.errorEl.getBoundingClientRect();
                if (box.top < 0 || box.bottom > window.innerHeight) {
                  self.errorEl.scrollIntoView({ block: 'center' });
                }
              }
            })
            .finally(function () {
              if (self.submitBtn) self.submitBtn.removeAttribute('aria-busy');
            });
        }

        /* gap1-2-03: table numbers ("Brojevi stolova") and bow splits ("Boje
           mašnica") are worked out from the quantity. Adding the very same
           configuration twice made Shopify merge both into one line (10
           pieces still reading "1 – 5"), so such a re-add is stopped with a
           message instead. Any other product skips the check entirely, and
           an unreadable cart never blocks the add. */
        duplicateCheck(body, cartUrl) {
          var derived = ['Brojevi stolova', 'Boje mašnica'];
          var props = {};
          var hasDerived = false;
          body.forEach(function (value, key) {
            var match = /^properties\[(.+)\]$/.exec(key);
            if (!match || typeof value !== 'string' || value.trim() === '') return;
            props[match[1]] = value;
            if (derived.indexOf(match[1]) !== -1) hasDerived = true;
          });
          if (!hasDerived) return Promise.resolve();

          var variantId = String(body.get('id') || '');
          var message = (this.errorEl && this.errorEl.dataset.duplicateError) || '';
          var keys = Object.keys(props);

          return fetch(cartUrl, { headers: { Accept: 'application/json' } })
            .then(function (response) {
              return response.json();
            })
            .then(
              function (cart) {
                var clash = (cart.items || []).some(function (item) {
                  if (String(item.variant_id) !== variantId) return false;
                  var own = item.properties || {};
                  var ownKeys = Object.keys(own).filter(function (k) {
                    return own[k] != null && String(own[k]).trim() !== '';
                  });
                  if (ownKeys.length !== keys.length) return false;
                  return keys.every(function (k) {
                    return String(own[k]) === props[k];
                  });
                });
                if (clash) throw new Error(message);
              },
              function () {}
            );
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     <mst-media-gallery>
     ------------------------------------------------------------------------ */
  if (!customElements.get('mst-media-gallery')) {
    customElements.define(
      'mst-media-gallery',
      class MstMediaGallery extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-gallery-track]');
          if (!this.track) return;

          this.items = Array.prototype.slice.call(
            this.track.querySelectorAll('[data-media-id]')
          );
          this.status = this.querySelector('[data-gallery-status]');
          this.counter = this.querySelector('[data-gallery-count]');
          this.desktopQuery =
            window.matchMedia && window.matchMedia('(min-width: 990px)');
          this.index = 0;
          this.scrollRaf = null;

          var self = this;

          // Arrows reflect the end state (aria-disabled at first/last image,
          // clicks guarded) — same pattern as <mst-fc-slider>.page().
          this.prev = this.querySelector('[data-gallery-prev]');
          this.next = this.querySelector('[data-gallery-next]');
          if (this.prev) {
            this.prev.addEventListener('click', function (event) {
              if (event.currentTarget.getAttribute('aria-disabled') === 'true') return;
              self.goTo(self.index - 1);
            });
          }
          if (this.next) {
            this.next.addEventListener('click', function (event) {
              if (event.currentTarget.getAttribute('aria-disabled') === 'true') return;
              self.goTo(self.index + 1);
            });
          }
          this.syncNav();

          this.onScrollBound = this.onScroll.bind(this);
          this.track.addEventListener('scroll', this.onScrollBound, {
            passive: true
          });

          // The track's tabindex="0" only makes sense while it is a
          // scrollable carousel (<990px); the desktop grid never scrolls,
          // so the tab stop is dropped there.
          this.onBreakpointBound = this.syncTrackTabindex.bind(this);
          this.syncTrackTabindex();
          if (this.desktopQuery) {
            if (this.desktopQuery.addEventListener) {
              this.desktopQuery.addEventListener('change', this.onBreakpointBound);
            } else if (this.desktopQuery.addListener) {
              this.desktopQuery.addListener(this.onBreakpointBound);
            }
          }
        }

        disconnectedCallback() {
          if (this.track && this.onScrollBound) {
            this.track.removeEventListener('scroll', this.onScrollBound);
          }
          if (this.desktopQuery && this.onBreakpointBound) {
            if (this.desktopQuery.removeEventListener) {
              this.desktopQuery.removeEventListener('change', this.onBreakpointBound);
            } else if (this.desktopQuery.removeListener) {
              this.desktopQuery.removeListener(this.onBreakpointBound);
            }
          }
        }

        syncTrackTabindex() {
          if (!this.track) return;
          if (this.isDesktop()) {
            this.track.removeAttribute('tabindex');
          } else if (this.items.length > 1) {
            this.track.setAttribute('tabindex', '0');
          }
        }

        syncNav() {
          var last = this.items.length - 1;
          if (this.prev) {
            this.prev.setAttribute(
              'aria-disabled',
              this.index === 0 ? 'true' : 'false'
            );
          }
          if (this.next) {
            this.next.setAttribute(
              'aria-disabled',
              this.index >= last ? 'true' : 'false'
            );
          }
        }

        behavior() {
          return prefersReducedMotion() ? 'auto' : 'smooth';
        }

        isDesktop() {
          return !!(this.desktopQuery && this.desktopQuery.matches);
        }

        clampIndex(index) {
          return Math.max(0, Math.min(this.items.length - 1, index));
        }

        goTo(index) {
          if (!this.items.length) return;
          index = this.clampIndex(index);
          var item = this.items[index];
          if (this.isDesktop()) {
            // Desktop 2-column grid: every tile is already visible — bring
            // the target tile into view (page scroll, no track scroll).
            if (item.scrollIntoView) {
              item.scrollIntoView({
                behavior: this.behavior(),
                block: 'nearest'
              });
            }
          } else {
            this.track.scrollTo({
              left: item.offsetLeft,
              behavior: this.behavior()
            });
          }
          this.setCurrent(index);
        }

        setActiveMedia(mediaId) {
          var id = String(mediaId);
          var index = this.items.findIndex(function (item) {
            return item.dataset.mediaId === id;
          });
          if (index > -1) this.goTo(index);
        }

        onScroll() {
          // Mobile carousel only — the desktop grid never scrolls its track.
          if (this.isDesktop()) return;
          var self = this;
          if (this.scrollRaf) return;
          this.scrollRaf = window.requestAnimationFrame(function () {
            self.scrollRaf = null;
            if (!self.items.length) return;
            var width = self.items[0].offsetWidth || 1;
            var index = self.clampIndex(Math.round(self.track.scrollLeft / width));
            self.setCurrent(index);
          });
        }

        setCurrent(index) {
          if (index === this.index && this.initialized) return;
          this.initialized = true;
          this.index = index;
          this.syncNav();

          var current = this.items[index];

          // Highlight the active tile (visible on the desktop grid only —
          // used to point at the selected variant's image).
          this.items.forEach(function (item) {
            item.classList.toggle('is-active', item === current);
          });

          if (this.counter) {
            this.counter.textContent = index + 1 + '/' + this.items.length;
          }

          if (this.status) {
            var template = this.status.dataset.statusTemplate || '';
            this.status.textContent = template.replace('__i__', index + 1);
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     <mst-sticky-atc>
     ------------------------------------------------------------------------ */
  if (!customElements.get('mst-sticky-atc')) {
    customElements.define(
      'mst-sticky-atc',
      class MstStickyAtc extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.section || '';
          this.realBtn = document.getElementById(
            'ProductSubmitButton-' + this.sectionId
          );
          this.trigger = this.querySelector('[data-sticky-trigger]');
          this.triggerLabel = this.querySelector('[data-sticky-label]');
          this.priceOut = this.querySelector('[data-sticky-price]');
          this.priceWrap = document.getElementById('price-' + this.sectionId);

          if (!this.realBtn || !this.trigger || !('IntersectionObserver' in window)) {
            this.setAttribute('hidden', '');
            return;
          }

          var self = this;

          this.onTriggerBound = function () {
            self.realBtn.click();
          };
          this.trigger.addEventListener('click', this.onTriggerBound);

          // Keep button state (sold out / unavailable) in sync after
          // variant changes — the picker updates the real button first.
          this.onVariantChangedBound = function () {
            self.syncButton();
          };
          document.addEventListener(
            'mst:variant:changed',
            this.onVariantChangedBound
          );

          this.syncButton();
          this.syncPrice();

          if (this.priceWrap && 'MutationObserver' in window) {
            this.priceObserver = new MutationObserver(function () {
              self.syncPrice();
            });
            this.priceObserver.observe(this.priceWrap, {
              childList: true,
              subtree: true,
              characterData: true
            });
          }

          this.intersectionObserver = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                var show = !entry.isIntersecting;
                self.classList.toggle('is-visible', show);
                self.setAttribute('aria-hidden', show ? 'false' : 'true');
              });
            },
            { rootMargin: '0px 0px -10% 0px' }
          );
          this.intersectionObserver.observe(this.realBtn);
        }

        disconnectedCallback() {
          if (this.trigger && this.onTriggerBound) {
            this.trigger.removeEventListener('click', this.onTriggerBound);
          }
          if (this.onVariantChangedBound) {
            document.removeEventListener(
              'mst:variant:changed',
              this.onVariantChangedBound
            );
          }
          if (this.priceObserver) this.priceObserver.disconnect();
          if (this.intersectionObserver) this.intersectionObserver.disconnect();
        }

        syncButton() {
          if (!this.trigger || !this.realBtn) return;
          this.trigger.disabled = this.realBtn.disabled;
          var realLabel = this.realBtn.querySelector('[data-submit-label]');
          if (this.triggerLabel && realLabel) {
            var text = (realLabel.textContent || '').replace(/\s+/g, ' ').trim();
            if (text) this.triggerLabel.textContent = text;
          }
        }

        syncPrice() {
          if (!this.priceOut || !this.priceWrap) return;
          var current = this.priceWrap.querySelector('.mst-price__current');
          var text = current ? (current.textContent || '').trim() : '';
          if (text) this.priceOut.textContent = text;
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     <mst-recommendations>
     ------------------------------------------------------------------------ */
  if (!customElements.get('mst-recommendations')) {
    customElements.define(
      'mst-recommendations',
      class MstRecommendations extends HTMLElement {
        connectedCallback() {
          this.url = this.dataset.url || '';
          if (!this.url) return;

          var self = this;
          if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(
              function (entries) {
                entries.forEach(function (entry) {
                  if (!entry.isIntersecting) return;
                  self.observer.disconnect();
                  self.load();
                });
              },
              { rootMargin: '0px 0px 400px 0px' }
            );
            this.observer.observe(this);
          } else {
            this.load();
          }
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }

        load() {
          if (this.loaded) return;
          this.loaded = true;
          var self = this;

          fetch(this.url)
            .then(function (response) {
              return response.text();
            })
            .then(function (text) {
              var doc = new DOMParser().parseFromString(text, 'text/html');
              var fresh = doc.querySelector('mst-recommendations');
              if (fresh && fresh.innerHTML.trim().length) {
                self.innerHTML = fresh.innerHTML;
              }
            })
            .catch(function () {
              /* recommendations are an enhancement — fail silently */
            });
        }
      }
    );
  }
})();
